/**
 * Chat Model Configuration
 *
 * This file contains the list of available AI models for the chat interface.
 * Supports multiple vendors: OpenAI and Groq.
 * Update this file when new models are released or deprecated.
 *
 * Latest model information:
 * - OpenAI: https://platform.openai.com/docs/models
 * - Groq: https://console.groq.com/docs/models
 * Last updated: 2025-12-13
 */

export interface ChatModel {
  id: string;
  name: string;
  description: string;
  vendor: 'openai' | 'groq';
  contextWindow?: number;
  supportsTools?: boolean;
}

export const CHAT_MODELS: readonly ChatModel[] = [
  // OpenAI Models (Updated from API 2025-12-13)

  // GPT-5 Series (Latest Generation)
  {
    id: 'gpt-5.2-pro',
    name: 'GPT-5.2 Pro',
    description: 'Latest Pro model - highest capability',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-5.2',
    name: 'GPT-5.2',
    description: 'Latest flagship - best for most tasks',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-5.1',
    name: 'GPT-5.1',
    description: 'GPT-5 series - excellent performance',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-5-pro',
    name: 'GPT-5 Pro',
    description: 'Pro variant - enhanced capabilities',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-5',
    name: 'GPT-5',
    description: 'GPT-5 base model - high performance',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-5-mini',
    name: 'GPT-5 Mini',
    description: 'Fast and efficient GPT-5 - great balance',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-5-nano',
    name: 'GPT-5 Nano',
    description: 'Ultra-fast GPT-5 - optimized for speed',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },

  // GPT-4.1 Series
  {
    id: 'gpt-4.1',
    name: 'GPT-4.1',
    description: 'GPT-4.1 base model - solid performance',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-4.1-mini',
    name: 'GPT-4.1 Mini',
    description: 'Efficient GPT-4.1 - good for most tasks',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'gpt-4.1-nano',
    name: 'GPT-4.1 Nano',
    description: 'Fast GPT-4.1 - optimized for speed',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },

  // o-Series (Advanced Reasoning Models)
  {
    id: 'o3',
    name: 'o3',
    description: 'Latest reasoning model - advanced problem solving',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'o3-mini',
    name: 'o3 Mini',
    description: 'Efficient reasoning model - good for coding and math',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'o1-pro',
    name: 'o1 Pro',
    description: 'Enhanced reasoning model - professional tasks',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },
  {
    id: 'o1',
    name: 'o1',
    description: 'Advanced reasoning model - excels at complex problems',
    vendor: 'openai',
    contextWindow: 128000,
    supportsTools: true,
  },

  // Groq Models (Updated from API 2025-12-12)

  // Groq Compound Series (Groq's own models)
  {
    id: 'groq/compound',
    name: 'Groq Compound',
    description: 'Groq flagship model - high performance',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },
  {
    id: 'groq/compound-mini',
    name: 'Groq Compound Mini',
    description: 'Smaller Groq model - fast and efficient',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },

  // Llama 4 Series (Latest - 2025)
  {
    id: 'meta-llama/llama-4-scout-17b-16e-instruct',
    name: 'Llama 4 Scout 17B',
    description: 'Latest Llama 4 model - excellent reasoning',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },
  {
    id: 'meta-llama/llama-4-maverick-17b-128e-instruct',
    name: 'Llama 4 Maverick 17B',
    description: 'Llama 4 variant - strong performance',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },

  // Llama 3.3 Series
  {
    id: 'llama-3.3-70b-versatile',
    name: 'Llama 3.3 70B',
    description: 'Most capable Llama 3 - excellent for complex tasks',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },

  // Llama 3.1 Series
  {
    id: 'llama-3.1-8b-instant',
    name: 'Llama 3.1 8B Instant',
    description: 'Ultra-fast model - optimized for speed',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },

  // Other Open Source Models
  {
    id: 'qwen/qwen3-32b',
    name: 'Qwen 3 32B',
    description: 'Alibaba Cloud model - multilingual support',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },
  {
    id: 'openai/gpt-oss-120b',
    name: 'GPT-OSS 120B',
    description: 'Large open source GPT - very capable',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },
  {
    id: 'moonshotai/kimi-k2-instruct',
    name: 'Kimi K2 Instruct',
    description: 'Moonshot AI model - strong instruction following',
    vendor: 'groq',
    contextWindow: 131072,
    supportsTools: true,
  },
] as const;

export const DEFAULT_MODEL = 'gpt-5-mini';

/**
 * Get model info by ID
 */
export function getModelInfo(modelId: string): ChatModel | undefined {
  return CHAT_MODELS.find((m) => m.id === modelId);
}

/**
 * Check if a model supports tool calling
 */
export function modelSupportsTools(modelId: string): boolean {
  const model = getModelInfo(modelId);
  return model?.supportsTools ?? true; // Default to true for unknown models
}
